import dayjs from 'dayjs'
import { router } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { FormattedMessage, type MessageDescriptor } from 'react-intl'
import { View } from 'react-native'

import { Image } from '@/src/elements/image'
import { GradientTypography } from '@/src/elements/text/gradient'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import type { TCommunityJoinParams } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useTimeout } from '@/src/general/hooks/useTimeout'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useRoutes } from '@/src/general/utils/routes'

import { useCommunityQuery } from '../../services/useCommunityQuery'

type TCommunityJoinCongratulationScreenProps = TIdSlugParam & TCommunityJoinParams

export const CommunityJoinCongratulationScreen = ({
  idSlug,
  course,
  origin,
  source,
}: TCommunityJoinCongratulationScreenProps): ReactNode => {
  const [id, slug] = parseIdDash(idSlug)

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const { routes } = useRoutes()

  const { data: community, refetch } = useCommunityQuery(id)
  const { data: user } = useMeQuery()

  useEffect(() => {
    GTM.sendEvent(EVENTS.COMMUNITY_JOIN_SUCCESS)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  let title: MessageDescriptor['id'] = 'community.join.congrats'
  if (community?.subscription) {
    title = 'community.join.congrats.comingSoon'
    const subscriptionFromDate = getDateWithoutTimezone(community?.subscription?.fromDate)
    if (subscriptionFromDate?.isSame(getDateWithoutTimezone(dayjs().format()), 'd')) {
      title = 'community.join.congrats.startToday'
      if (!subscriptionFromDate.isSame(getDateWithoutTimezone(community?.fromDate), 'd')) {
        title = 'community.join.congrats.ongoing'
      }
    }
  }

  useTimeout(() => router.navigate(routes.communityJoinNext({ id, slug, course, origin, source })), 3000)

  return (
    <>
      <View
        testID={TEST_ID.COMMUNITY.JOIN_CONGRATULATION_SCREEN}
        className="mx-4 mt-10 max-w-screen-md self-center pt-safe pb-safe"
      >
        <View className="max-h-[300px] w-full" style={{ aspectRatio: width && height ? width / height : 16 / 9 }}>
          <Image
            source={require('@/img/prayer/raise_hand_prayer.png')}
            onLoad={(event: { source: { width: number; height: number } }) => {
              const { source: imageSource } = event
              if (!width) {
                setWidth(imageSource.width)
              }
              if (!height) {
                setHeight(imageSource.height)
              }
            }}
          />
        </View>
        <GradientTypography colors="primary" size="xl" className="text-center font-black">
          <FormattedMessage
            id={title}
            values={{
              userName: user?.name,
              lifetime: community?.lifetime,
              date: getDateWithoutTimezone(community?.subscription?.fromDate)?.format('D MMMM'),
            }}
          />
        </GradientTypography>
      </View>
    </>
  )
}
